import {
  Body,
  Button,
  Container,
  Head,
  Heading,
  Html,
  Section,
  Text,
} from 'react-email';
import { render } from '@react-email/render';
import * as React from 'react';
import { EMAIL_FOOTER_MODERN_PLACEHOLDER } from '../templates/sharedTemplates';

export interface ClientIntakeFormEmailProps {
  advisorFirstName: string;
  intakeFormLink: string;
  advisorProvidedBody?: string;
  clientFirstName?: string;
  /** Sanitized HTML; replaces the default "Best, {firstName}" sign-off. */
  advisorSignatureHtml?: string;
  isPreview?: boolean;
}

const FONT_FAMILY = "'Helvetica Neue',Helvetica,Arial,sans-serif";

const PURPLE = '#7755CC';
const PURPLE_DARK = '#392C77';
const INK = '#16113A';
const TEXT = '#3F3F56';
const MUTED = '#8886A5';

const bodyStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  backgroundColor: '#EFEDF9',
  margin: 0,
  padding: '24px 0',
};

const containerStyle: React.CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  padding: '40px',
};

const wordmarkStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: '26px',
  fontWeight: 700,
  color: PURPLE_DARK,
  margin: '0 0 24px',
};

const headingStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: '26px',
  fontWeight: 700,
  color: INK,
  margin: '0 0 16px',
};

const paragraphStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: '15px',
  lineHeight: '1.6em',
  color: TEXT,
  margin: '0 0 20px',
};

const buttonStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: '15px',
  fontWeight: 700,
  color: '#FFFFFF',
  backgroundColor: PURPLE,
  borderRadius: '9999px',
  textDecoration: 'none',
  textAlign: 'center',
  display: 'inline-block',
  padding: '14px 32px',
};

const copyrightStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: '14px',
  color: MUTED,
  textAlign: 'center',
  margin: '32px 0 0',
};

function getDefaultMessage(clientFirstName?: string) {
  const greeting = clientFirstName && clientFirstName !== '' ? `Hi ${clientFirstName},\n\n` : '';
  return `${greeting}As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.\n\nI use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.\n\nThe first step is to complete the intake form below (it’s about 5 minutes long) and then we get the predictions that we can work with and build from there.`;
}

function TextWithLineBreaks({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {index > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
    </>
  );
}

export function ClientIntakeFormEmail({
  advisorFirstName,
  intakeFormLink,
  advisorProvidedBody,
  clientFirstName,
  advisorSignatureHtml,
  isPreview = false,
}: ClientIntakeFormEmailProps) {
  const message =
    advisorProvidedBody && advisorProvidedBody.trim().length > 0
      ? advisorProvidedBody
      : getDefaultMessage(clientFirstName);

  return (
    <Html>
      <Head>
        <title>Waterlily - Start Creating Your Long-Term Care Plan</title>
      </Head>
      <Body style={bodyStyle}>
        <Container style={containerStyle}>
          <Section style={cardStyle}>
            <Text style={wordmarkStyle}>Waterlily</Text>
            <Heading
              as="h1"
              style={headingStyle}
            >
              Start your long-term care plan
            </Heading>
            <Text style={paragraphStyle}>
              <TextWithLineBreaks text={message} />
            </Text>
            <Section style={{ padding: '4px 0 24px' }}>
              {isPreview ? (
                <span style={{ ...buttonStyle, cursor: 'default', opacity: 0.8 }}>
                  Start Intake Form
                </span>
              ) : (
                <Button
                  href={intakeFormLink}
                  style={buttonStyle}
                >
                  Start Intake Form
                </Button>
              )}
            </Section>
            {advisorSignatureHtml ? (
              <div
                style={{ ...paragraphStyle, margin: '0 0 32px' }}
                dangerouslySetInnerHTML={{ __html: advisorSignatureHtml }}
              />
            ) : (
              <Text style={{ ...paragraphStyle, margin: '0 0 32px' }}>
                Best, <br /> {advisorFirstName}
              </Text>
            )}
          </Section>
          <Text style={copyrightStyle}>
            © {new Date().getFullYear()} Waterlily. All Rights Reserved.
          </Text>
          {/* The send path replaces this comment with the unsubscribe footer;
              React can't emit HTML comments directly, hence the raw injection. */}
          <div dangerouslySetInnerHTML={{ __html: EMAIL_FOOTER_MODERN_PLACEHOLDER }} />
        </Container>
      </Body>
    </Html>
  );
}

export function renderClientIntakeFormEmail(props: ClientIntakeFormEmailProps): Promise<string> {
  return render(<ClientIntakeFormEmail {...props} />);
}
